import { ChangeDetectionStrategy, Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { appMatDialogDefaultConfig, isNotNullOrUndefined, TableColumnConfig } from 'src/app/helper/helper';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    firstValueFrom,
    map,
    Observable,
    shareReplay,
    Subscription,
    switchMap,
    tap,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from 'src/app/dave-data-module/State';
import { getEmployees } from 'src/app/dave-data-module/State/selectors/employees.selectors';
import {
    getUserToCommissionByCommissionId,
    getUserToCommissionFetched,
} from 'src/app/dave-data-module/State/selectors/user-to-commission.selector';
import {
    getUserToCommissionShiftsActive,
} from 'src/app/dave-data-module/State/selectors/users-to-commission-shift.selectors';
import { EmployeeEntity } from 'src/app/dave-data-module/entities/employee.entity';
import {
    getClockInTypes,
    getClockInTypesFetched,
} from 'src/app/dave-data-module/State/selectors/clock-in-type.selectors';
import { ClockInTypeResolver } from 'src/app/dave-data-module/guards/clock-in-type.resolver';
import { getWorkedTimes, getWorkedTimesFetched } from 'src/app/dave-data-module/State/selectors/worked-times.selectors';
import { WorkedTimesResolver } from 'src/app/dave-data-module/guards/worked-times.resolver';
import { ClockInTypeEntity, EnumClockInTypeSlug } from 'src/app/dave-data-module/entities/clock-in-type.entity';
import { getWorkDays } from 'src/app/dave-data-module/State/selectors/work-day.selectors';
import { UserToCommissionResolver } from 'src/app/dave-data-module/guards/user-to-commission.resolver';
import { WorkDayEntity } from 'src/app/dave-data-module/entities/work-day.entity';
import { WorkedTimesEntity } from 'src/app/dave-data-module/entities/worked-times.entity';
import { User2CommissionShiftEntity } from 'src/app/dave-data-module/entities/user2CommissionShift.entity';
import {
    getJobSpecification,
    getJobSpecificationDictionary,
} from 'src/app/dave-data-module/State/selectors/job-specification.selector';
import { JobSpecificationEntity } from 'src/app/dave-data-module/entities/job-specification.entity';
import { formatCurrency, formatNumber } from '@angular/common';
import { UserToCommissionEntity } from 'src/app/dave-data-module/entities/user-to-commission.entity';
import { getPartner } from 'src/app/dave-data-module/State/selectors/partners.selectors';
import { FilterTypes } from 'src/app/services/default-filter.service';
import { getFetched$ } from 'src/app/dave-data-module/helper/helper';
import {
    FILTER_TYPE_BOOLEAN,
    FILTER_TYPE_SEARCH_MULTI_SELECT, FILTER_TYPE_TIMESPAN,
    IFilterTypeSearchMultiSelectValue,
} from 'src/app/dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { getEntityRole, getEntityRoleFetched } from 'src/app/dave-data-module/State/selectors/entity-role.selector';
import { EntityTypesEnum } from 'src/app/dave-data-module/entities/entity-role.entity';
import { CommissionEntity } from '../../../../../dave-data-module/entities/commission.entity';
import {
    getCommissionDictionary, getCommissions,
    getCommissionsActive,
} from '../../../../../dave-data-module/State/selectors/commission.selector';
import { distinctUntilChanged } from 'rxjs/operators';
import { CustomLabelService } from '../../../../../services/custom-label/custom-label.service';
import { Moment } from 'moment/moment';
import moment from 'moment';

const clockInTypeColumnKeyPrefix = 'cType-';

export interface UserToCommissionListComponentDialogData {
    commissionId?: number;
    filteredSubCommissions?: number[];
}
interface TableData {
    employeeId: number
    userName: string
    plannedHours: string;
    deviation: string;
    actualHours: string;
    cost: string;
    cTypes$: Map<string, string>;
}
@Component({
    selector: 'app-user-to-commission-list',
    templateUrl: './user-to-commission-list.component.html',
    styleUrls: ['./user-to-commission-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserToCommissionListComponent implements OnDestroy {
    public static DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        maxWidth: '100%',
        minWidth: '100%',
        minHeight: 'calc(100vh - 15rem)',
    };
    public TableColumns: TableColumnConfig<TableData>[] = [
        { header: 'Mitarbeiter', name: 'userName' },
        { header: 'Soll-Stunden', name: 'plannedHours' },
        { header: 'Abweichung(+/-)', name: 'deviation' },
        { header: 'Ist-Stunden', name: 'actualHours' },
        { header: 'Kosten', name: 'cost' },
    ];
    public FilterValues$ = new BehaviorSubject<{
        [FilterTypes.Roles]: IFilterTypeSearchMultiSelectValue<number>[];
        [FilterTypes.SubCommissions]: IFilterTypeSearchMultiSelectValue<number>[];
        [FilterTypes.ActualHours]: boolean;
        [FilterTypes.Date]:  { to: Moment; from: Moment };
    }> ({
        [FilterTypes.Roles]: [],
        [FilterTypes.SubCommissions]: [],
        [FilterTypes.ActualHours]: false,
        [FilterTypes.Date]: {from: null, to: null},
    });
    private commissionIdFilter$: Observable<number[]> = this.FilterValues$.pipe(
        map(f => [...(f[FilterTypes.SubCommissions]?.map(({id}) => id) || [])]),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        shareReplay({refCount: true, bufferSize: 1}),
    );

    private userToCommissionShifts$: Observable<User2CommissionShiftEntity[]> = getFetched$(this.store, getUserToCommissionFetched, getUserToCommissionShiftsActive, this.u2cResolver).pipe(
        switchMap(utc =>
            this.commissionIdFilter$.pipe(
                map(cIds =>
                    cIds.length === 0
                        ? utc
                        : utc.filter(u => cIds.includes(u.CommissionId))
                )
            )
        )
    );
    private workedTimes$: Observable<WorkedTimesEntity[]> = getFetched$(this.store, getWorkedTimesFetched, getWorkedTimes, this.workedTimesResolver).pipe(
        switchMap(workTimes =>
            this.commissionIdFilter$.pipe(
                map(cIds =>
                    cIds.length === 0
                        ? workTimes
                        : workTimes.filter(w => cIds.includes(w.CommissionId))
                )
            )
        )
    );
    private workedDays$: Observable<WorkDayEntity[]> = combineLatest([
        this.store.select(getWorkDays),
        this.workedTimes$,
    ]).pipe(
        map(([workedDays, workedTimes]) => {
            const WorkDayIds: number[] = workedTimes.map((c) => {return c.WorkDayId})
            return workedDays.filter((workDay) => WorkDayIds.includes(workDay.Id))
        })
    );
    private clockInTypes$: Observable<ClockInTypeEntity[]> = this.store.select(getClockInTypesFetched).pipe(
        tap((fetched) => {
            if (!fetched) {
                this.clockInTypeResolver.resolve();
            }
        }),
        filter((fetched) => !!fetched),
        switchMap(() => combineLatest([this.store.select(getClockInTypes), this.store.select(getPartner)])),
        map(([types, settings]) =>
            types.filter((t) => t.IsWorkingTime && !(settings?.Settings?.hiddenDefaultClockInTypes?.includes(t.Id))
        )),
    );
    private commissionEntityRoles$ = getFetched$(this.store, getEntityRoleFetched, getEntityRole).pipe(map((roles) => roles.filter((r) => r.Entity === EntityTypesEnum.Commission)));
    public availableColumns: Array<keyof TableData> = ['userName', 'plannedHours', 'deviation', 'actualHours', 'cost'];
    public clockInTypeColumns$: Observable<Array<{columnName: string; header: string}>> = this.clockInTypes$.pipe(
        map(cTypes => {
            let workingType: Array<{columnName: string; header: string}> = cTypes.filter(c => c.Slug === EnumClockInTypeSlug.workTime).map(c => ({
                columnName: clockInTypeColumnKeyPrefix + c.Id,
                header: c.Name,
            }))
            let types: Array<{columnName: string; header: string}> =  cTypes.filter(c => c.Slug !== EnumClockInTypeSlug.workTime).map(c => ({
                columnName: clockInTypeColumnKeyPrefix + c.Id,
                header: c.Name,
            }))
            return (workingType && types) ? types.concat(workingType) : types || workingType || null
        }
    ));
    public displayedColumns$: Observable<Array<String>> = this.clockInTypeColumns$.pipe(
        map(cTypes => {
            return [...this.availableColumns, ...cTypes.map(c=> c.columnName)]
        }),
        shareReplay({refCount: true, bufferSize: 1}),
    );
    public DataSource$: Observable<MatTableDataSource<TableData>>;

    FilterSettings$: Observable<any>;
    public FilterAmount$ = this.FilterValues$.pipe(map((filter) => {
        let count = 0;
        const hasDateRange = (value: { from?: any; to?: any } | null | undefined) =>
            !!value && (value.from || value.to);

        if (hasDateRange(filter[FilterTypes.Date])) {
            count += 1;
        }
        if (filter[FilterTypes.Roles]?.length) {
            count++;
        }
        if (!filter[FilterTypes.ActualHours]) {
            count++;
        }
        return count || null;
    }));
    private subscriptions: Subscription[] = [];

    private commissions$: Observable<CommissionEntity[]>;
    private readonly commissionId: number;
    constructor(
        @Inject(MAT_DIALOG_DATA) DialogData: UserToCommissionListComponentDialogData,
        protected store: Store<State>,
        private workedTimesResolver: WorkedTimesResolver,
        private clockInTypeResolver: ClockInTypeResolver,
        private u2cResolver: UserToCommissionResolver,
        @Inject(LOCALE_ID) private locale: string,
        cls: CustomLabelService,
    ) {
        this.commissionId = DialogData.commissionId;
        this.commissions$ = this.store.select(getCommissions).pipe(map(commissions => commissions.filter(c => c.ParentId === this.commissionId || c.Id === this.commissionId)));
        this.FilterSettings$ = combineLatest([
            this.commissionEntityRoles$,
            this.commissions$,
            cls.getMultiple$('Commission'),]).pipe(
            map(([roles, childCommissions, commissionLabel]) => {
                return [
                    {
                        Name: FilterTypes.Roles,
                        Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                        Label: 'Rolle',
                        Icon: null,
                        Values: roles.map<IFilterTypeSearchMultiSelectValue>((t) => ({ id: t.Id, label: t.DisplayName })),
                    },
                    {
                        Name: FilterTypes.ActualHours,
                        Type: FILTER_TYPE_BOOLEAN,
                        Label: 'Einträge ohne Ist-Stunden anzeigen',
                    },
                    {
                        Name: FilterTypes.SubCommissions,
                        Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                        Label: commissionLabel,
                        Values: childCommissions.map((c) => ({
                            label: c.optionLabel,
                            id: c.Id,
                        })),
                    },
                    {
                        Name: FilterTypes.Date,
                        Type: FILTER_TYPE_TIMESPAN,
                        Label: 'Zeitraum',
                    },
                ];
            }),
        );

        /* Filter Standard Werte setzen */
        firstValueFrom(combineLatest([this.commissionEntityRoles$, this.store.select(getCommissionDictionary)])).then(([roles, commissions]) => {
            this.FilterValues$.next({
                [FilterTypes.Roles]: roles.filter((r) => r.DisplayName !== 'Admin').map((r) => ({ id: r.Id, label: r.DisplayName })),
                [FilterTypes.ActualHours]: false,
                [FilterTypes.SubCommissions]: DialogData?.filteredSubCommissions ? DialogData.filteredSubCommissions.map((id) => ({ id, label : commissions[id]?.optionLabel })) : [],
                [FilterTypes.Date]: {from: null, to: null},
            });
        });
        /* Tabellen Daten ausrechnen */
        this.DataSource$ = combineLatest([
            this.userToCommissionShifts$,
            this.workedTimes$,
            this.workedDays$,
            this.clockInTypes$,
            this.FilterValues$,
            this.commissionIdFilter$.pipe(switchMap(cids => combineLatest(cids.map(cid => this.store.select(getUserToCommissionByCommissionId({commissionId: cid})))))),
            this.store.select(getEmployees).pipe(map((employees) => employees.filter((e) => e.UserId))),
            this.store.select(getJobSpecification),
            this.store.select(getJobSpecificationDictionary),
        ]).pipe(
            map(([u2cs, workedTimes, workedDays, clockInTypes, filterValues,  u2c, employees, job, jobSpecification]) => {
                let filteredUsers: UserToCommissionEntity[] = u2c.flat();
                if (filterValues[FilterTypes.Roles]?.length) {
                    filteredUsers = filteredUsers.filter((u) => filterValues[FilterTypes.Roles].some((f) => f.id === u.RoleId));
                }

                const hasDateRange = (value: { from?: any; to?: any } | null | undefined) =>
                    !!value && (value.from || value.to);

                if (hasDateRange(filterValues[FilterTypes.Date])) {
                    workedDays = workedDays.filter((wd) => {
                        const date = moment.utc(wd.Timestamp);
                        return (
                            (!filterValues[FilterTypes.Date]?.from ||
                                (filterValues[FilterTypes.Date].from as Moment)
                                    .isSameOrBefore(date, 'day')) &&

                            (!filterValues[FilterTypes.Date]?.to ||
                                (filterValues[FilterTypes.Date].to as Moment)
                                    .isSameOrAfter(date, 'day'))
                        );
                    });
                }


                const uniqueUsers: UserToCommissionEntity[] = filteredUsers.reduce((accumulator, current) => {
                    if (!accumulator.find((item) => item.UserId === current.UserId)) {
                      accumulator.push(current);
                    }
                    return accumulator;
                  }, []
                );

                return new MatTableDataSource(
                    uniqueUsers.map<TableData>((u2c) => {
                        const Employee: EmployeeEntity = employees.find((e) =>
                            u2c.UserId === e.UserId,
                        );
                        if (!Employee){
                            return null;
                        }
                        const EmployeeWorkedDays: WorkDayEntity[] =  workedDays.filter((workDay) =>
                            workDay.EmployeeId === Employee.Id,
                        );
                        const EmployeeWorkedTimes: WorkedTimesEntity[] = workedTimes.filter((workTime) => {
                            const WorkDayIds: number[] = EmployeeWorkedDays.map((workDay) => {return workDay.Id})
                            return WorkDayIds.includes(workTime.WorkDayId)
                        });
                        const TotalActualHours: number = EmployeeWorkedTimes.filter((e) => {
                            const clockTypesIds: number[] = clockInTypes.map((type) => {return type.Id})
                            return clockTypesIds.includes(e.ClockInTypeId)
                        }).reduce((prev, curr) => prev + curr.Timespan, 0) / 1000 / 60 / 60;
                        if (!filterValues[FilterTypes.ActualHours] && TotalActualHours === 0) {
                           return null;
                        }

                        const Employee2CommissionShift: User2CommissionShiftEntity[] = u2cs.filter((u) =>
                            u.UserId === u2c.UserId
                        );
                        const EmployeeJob: JobSpecificationEntity = (Employee.JobSpecificationId
                            ? job.find((j) => j.Id === jobSpecification[Employee.JobSpecificationId]?.Id)
                            : null
                        );
                        const TotalPlannedHours: number = Employee2CommissionShift.reduce((prev, curr) =>
                            prev + (curr.PlannedHours || 0), 0
                        );

                        const Cost: string = (EmployeeJob
                            ? formatCurrency((EmployeeJob.Amount || 0) * TotalActualHours, this.locale, '€', 'EUR')
                            : '0,00 €'
                        );
                        const ClockInTypesActualHours = (c:ClockInTypeEntity) => {
                            const EmployeeFilteredToClockType: WorkedTimesEntity[] = EmployeeWorkedTimes.filter((e) =>
                                e.ClockInTypeId === c.Id
                            )
                            return EmployeeFilteredToClockType.reduce((prev, curr) => Number.parseFloat((prev).toFixed(2)) + Number.parseFloat((curr.Timespan / 1000 / 60 / 60 ).toFixed(2)), 0);
                        };
                        const ClockInTypesCost = (c: number) => {
                            return EmployeeJob ? formatCurrency(EmployeeJob.Amount * c, this.locale, '€', 'EUR') : '0,00 €'
                        }

                        return {
                            employeeId: Employee.Id,
                            userName: Employee.Lastname + ", " + Employee.Firstname,
                            plannedHours: formatNumber(TotalPlannedHours, this.locale, '1.2-2') + ' Std.',
                            deviation: (TotalPlannedHours - TotalActualHours).toLocaleString('de', {minimumFractionDigits : 2, maximumFractionDigits: 2}) + ' Std.',
                            actualHours: TotalActualHours.toLocaleString('de', {minimumFractionDigits : 2, maximumFractionDigits: 2}) + ' Std.',
                            cost: Cost,
                            cTypes$: new Map<string, string>(clockInTypes.map(c => {
                                let clockInTypesActualHour = ClockInTypesActualHours(c);
                                return [clockInTypeColumnKeyPrefix+c.Id, clockInTypesActualHour.toLocaleString('de', {minimumFractionDigits : 2, maximumFractionDigits: 2}) + ' Std.' + ' (' + ClockInTypesCost(clockInTypesActualHour) + ')']
                            }))
                        }
                    }).filter(isNotNullOrUndefined),
                );
            }),
        )
        shareReplay({ refCount: true, bufferSize: 1 })
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    public getTotal(collumn: TableColumnConfig<TableData>, data: MatTableDataSource<TableData>): string {
        let collumnData: number[] = null;
        if(collumn.name === 'userName') {
            return 'Gesamt:'
        }
        else if(data.data.length > 0) {
            collumnData = data.data.map((d) => {
                switch (collumn.name) {
                    case 'actualHours': return Number.parseFloat(d.actualHours.replace(/[.]/g,'').replace(',','.'))
                    case 'plannedHours': return Number.parseFloat(d.plannedHours.replace(/[.]/g,'').replace(',','.'))
                    case 'deviation': return Number.parseFloat(d.deviation.replace(/[.]/g,'').replace(',','.'))
                    case 'cost': return Number.parseFloat(d.cost.replace(/[.]/g,'').replace(',','.'))
                    default : return null
                }
            });
        }
        if(collumn.name === 'cost') {
            if(collumnData !== null) {
                const totalCost = collumnData.reduce((prev, curr) =>  prev + curr, 0);
                return formatCurrency(totalCost, 'de-DE', '€', 'EUR')
            } else {
            return '0,00 €'
            }
        }
        else if(collumnData !== null) {
            return collumnData.reduce((prev, curr) =>  prev + curr, 0).toLocaleString('de', {minimumFractionDigits : 2, maximumFractionDigits: 2}) + ' Std.'
        }
        else {
            return '0,00 Std.'
        }
    }

    public getTotalCType(collumns: string, data: MatTableDataSource<TableData>): string {
        var totalActualHours: number = 0;
        var totalCost: number = 0;
        data.data.map((d) => {
            const collumn: string = d.cTypes$.get(collumns);
            const values: Array<string> = collumn.replace(/[.()Std€]/g,'').replace(/,/g,'.').split(' ').filter((v) => v);
            totalActualHours += Number(values[0]);
            totalCost += Number(values[1]);
        });
        return totalActualHours.toLocaleString('de', {minimumFractionDigits : 2, maximumFractionDigits: 2}) + ' Std.' + ' (' + formatCurrency(totalCost, 'de-DE', '€', 'EUR') + ')'
    }
}
